import React, { useState, useEffect } from 'react'
import { AuthProvider } from '../types'

export interface SignInProps {
  providers: AuthProvider[]
  onSuccess?: (provider: string, data: any) => void
  onError?: (error: Error) => void
  className?: string
  style?: React.CSSProperties
}

export function SignIn({ providers, onSuccess, onError, className, style }: SignInProps) {
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleProviderClick = async (provider: AuthProvider) => {
    setLoading(provider.id)
    setError(null)

    try {
      if (provider.type === 'oauth') {
        // Redirect to OAuth provider
        const params = new URLSearchParams({
          client_id: provider.clientId!,
          redirect_uri: provider.redirectUri || window.location.origin + '/auth/callback',
          response_type: 'code',
          scope: provider.scopes?.join(' ') || 'openid profile email',
          state: generateState()
        })

        const authUrl = getOAuthUrl(provider.id, params)
        window.location.href = authUrl
      } else if (provider.type === 'email') {
        // Handle email authentication
        const email = prompt('Enter your email address:')
        if (email) {
          // In a real implementation, you'd send a magic link
          onSuccess?.(provider.id, { email })
        }
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Authentication failed')
      setError(error.message)
      onError?.(error)
    } finally {
      setLoading(null)
    }
  }

  const getOAuthUrl = (providerId: string, params: URLSearchParams): string => {
    const baseUrls: Record<string, string> = {
      google: 'https://accounts.google.com/o/oauth2/v2/auth',
      github: 'https://github.com/login/oauth/authorize',
      microsoft: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
      discord: 'https://discord.com/oauth2/authorize'
    }

    const baseUrl = baseUrls[providerId]
    if (!baseUrl) {
      throw new Error(`Unsupported OAuth provider: ${providerId}`)
    }

    return `${baseUrl}?${params.toString()}`
  }

  const generateState = (): string => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36)
  }
  const getProviderIcon = (providerId: string): string => {
    const icons: Record<string, string> = {
      google: 'G',
      github: 'GH',
      microsoft: 'MS',
      discord: 'DC',
      email: '??'
    }
    return icons[providerId] || 'Auth'
  }

  const getProviderName = (providerId: string): string => {
    const names: Record<string, string> = {
      google: 'Google',
      github: 'GitHub',
      microsoft: 'Microsoft',
      discord: 'Discord',
      email: 'Email'
    }
    return names[providerId] || providerId
  }

  return (
    <div className={`signin-container ${className || ''}`} style={style}>
      <div className="signin-header">
        <h2>Sign In</h2>
        <p>Choose your preferred sign-in method</p>
      </div>

      {error && (
        <div className="signin-error">
          <p>{error}</p>
        </div>
      )}

      <div className="signin-providers">
        {providers.map((provider) => (
          <button
            key={provider.id}
            className={`signin-provider ${loading === provider.id ? 'loading' : ''}`}
            onClick={() => handleProviderClick(provider)}
            disabled={loading === provider.id}
          >
            <span className="provider-icon">
              {getProviderIcon(provider.id)}
            </span>
            <span className="provider-name">
              {loading === provider.id ? 'Signing in...' : `Sign in with ${getProviderName(provider.id)}`}
            </span>
          </button>
        ))}
      </div>

      <style>{`
        .signin-container {
          max-width: 400px;
          margin: 0 auto;
          padding: 2rem;
          border: 1px solid #e1e5e9;
          border-radius: 8px;
          background: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .signin-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .signin-header h2 {
          margin: 0 0 0.5rem 0;
          color: #1a1a1a;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .signin-header p {
          margin: 0;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .signin-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          padding: 0.75rem;
          margin-bottom: 1rem;
        }

        .signin-error p {
          margin: 0;
          color: #dc2626;
          font-size: 0.875rem;
        }

        .signin-providers {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .signin-provider {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          color: #374151;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .signin-provider:hover:not(:disabled) {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .signin-provider:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .signin-provider.loading {
          background: #f3f4f6;
        }

        .provider-icon {
          font-size: 1.25rem;
        }

        .provider-name {
          flex: 1;
          text-align: left;
        }
      `}</style>
    </div>
  )
}
